// /agents/:agentId — one project agent: a header (icon, name and its key to copy, description; what it works over,
// its model, who can use it and when it last changed), with "Change with the builder", "Chat" and a ⋮ menu (its name
// and description, who can use it, Remove), then its tabs, Instructions first. Its instructions, model, tools and
// guardrails change on their tabs (each saved with the agent, one commit). The chat opens on the right, over the
// page, with its tests' questions to start from. Removing it says first where it is still used.
import { useConfirm } from '@cortex/ui';
import {
  AppWindow,
  ArrowLeft,
  Check,
  Copy,
  Cpu,
  FileCheck2,
  Globe,
  Loader2,
  Lock,
  MessageSquare,
  MoreVertical,
  Pencil,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  agentRunsKey,
  useAgentsKey,
  useAgentsHost,
  useAgentsQuery as useApiQuery,
  useInvalidate,
} from './host.tsx';
import { AgentIcon } from './AgentIcon.tsx';
import { ShareDialog } from './dialogs.tsx';
import { useEvalsKey } from './Evaluations.tsx';
import { ago, roleLabel, type Agent } from './model.ts';
import { TestChat } from './TestChat.tsx';
import { useAgentCatalog, useAgentUsage } from './catalog.tsx';
import { AgentTabs } from './tabs.tsx';
import { btnGhost, btnPrimary, iconBtn, input, messageOf, Modal, unwrap } from './ui.tsx';

const A = '/agents' as const;

const menuItem =
  'flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-sm border-0 bg-transparent cursor-pointer hover:bg-page-bg';

/** Its name, icon, description and the requirements it serves: saved with the agent, one commit. */
function NameDialog({
  agent,
  onSave,
  onClose,
}: {
  agent: Agent;
  onSave(body: {
    title: string;
    icon: string | null;
    description: string | null;
    requirements: string[];
  }): Promise<void>;
  onClose(): void;
}) {
  const [title, setTitle] = React.useState(agent.title);
  const [icon, setIcon] = React.useState(agent.icon ?? '');
  const [description, setDescription] = React.useState(agent.description ?? '');
  const [requirements, setRequirements] = React.useState(
    agent.covers
      .filter((c) => c.kind === 'requirement')
      .map((c) => c.key)
      .join(', '),
  );
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const save = async () => {
    if (!title.trim()) return setErr('The agent needs a name.');
    setBusy(true);
    setErr('');
    try {
      await onSave({
        title: title.trim(),
        icon: icon.trim() || null,
        description: description.trim() || null,
        requirements: requirements
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={
        <>
          <Pencil size={16} /> Name and description
        </>
      }
      onClose={onClose}
      testId="agent-name-dialog"
      footer={
        <>
          <button type="button" className={btnGhost} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={btnPrimary}
            data-testid="agent-save"
            onClick={() => void save()}
            disabled={busy}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex gap-2">
          <input
            className={input + ' w-20'}
            data-testid="agent-field-icon"
            value={icon}
            maxLength={16}
            placeholder="🤖"
            onChange={(e) => setIcon(e.target.value)}
          />
          <input
            className={input + ' font-semibold'}
            data-testid="agent-field-display-name"
            value={title}
            maxLength={120}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <label className="block">
          <span className="text-xs text-page-muted">Description — what it answers, for whom</span>
          <textarea
            className={input + ' min-h-[64px]'}
            data-testid="agent-field-description"
            value={description}
            maxLength={4000}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-xs text-page-muted">Requirements it serves (keys, comma-separated)</span>
          <input
            className={input + ' font-mono'}
            data-testid="agent-field-requirements"
            value={requirements}
            onChange={(e) => setRequirements(e.target.value)}
          />
        </label>
        {err ? (
          <div className="text-sm text-red-600" data-testid="agent-save-error">
            {err}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

/** One project agent's page (the host's `/agents/:key`). */
export function AgentPage({
  agentKey: agentId,
  embedded = false,
}: {
  agentKey: string;
  /** Inside the Agents page's layout (its rail beside it): no "Back to Agents". */
  embedded?: boolean;
}) {
  const host = useAgentsHost();
  const api = host.client.fetch;
  const { queryClient } = host;
  const invalidate = useInvalidate();
  const k = useAgentsKey();
  const ek = useEvalsKey();
  const path = React.useMemo(() => ({ key: agentId }), [agentId]);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const [err, setErr] = React.useState('');
  const [saved, setSaved] = React.useState(false);
  const [naming, setNaming] = React.useState(false);
  const [sharing, setSharing] = React.useState(false);
  const [changing, setChanging] = React.useState(false);
  const [menu, setMenu] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const chatOpen = params.get('chat') === '1';
  // ADR 0038: the agent in the one manifest shape (its source, its tabs).
  const agentDocs = useAgentCatalog();
  const doc = agentDocs.data?.agents?.find((a) => a.kind === 'project' && a.key === agentId) ?? null;
  const usage = useAgentUsage();
  // No tab named: the first (Instructions).
  const tab = params.get('tab') ?? '';
  const setTab = (id: string) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.set('tab', id);
        return n;
      },
      { replace: true },
    );

  // The agent and its runs live in the shared query cache: the Agents list, a chat tool that changed
  // the app's agents, or a delegation to this agent from the project chat marks them stale.
  const agentKey = k(agentId);
  const loaded = useApiQuery(agentKey, () => unwrap(api.GET(`${A}/{key}`, { params: { path } })));
  const agent: Agent | null = loaded.data;
  const loadErr = loaded.error ?? '';
  /** A write returned the agent: show it at once, and the Agents list reloads. */
  const setAgent = (a: Agent) => {
    queryClient.setQueryData(agentKey, a);
    invalidate([k()]);
  };
  // The concrete model its kind resolves to here (the header names it), and its tests' questions (the chat's).
  const models = useApiQuery([...host.cache.agents, 'agent-models'], () => host.models!.resolve(), {
    enabled: !!host.models,
  });
  const suites = useApiQuery(ek('suites'), () => host.evals!.suites(), {
    enabled: !!host.evals,
  });

  // A chat turn adds a run: the Runs tab reads its runs again.
  const reloadRuns = () =>
    invalidate([
      agentRunsKey(host.cache.runs, agentId),
      agentRunsKey(host.cache.runs, `agent-${agentId}`),
      k('asks', agentId),
    ]);

  React.useEffect(() => {
    setSaved(false);
    setErr('');
    setMenu(false);
  }, [agentId]);
  // A new agent arrives with `edit=1` (the create dialog): its page opens on Instructions; the flag is dropped.
  React.useEffect(() => {
    if (params.get('edit') !== '1') return;
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        next.delete('edit');
        return next;
      },
      { replace: true },
    );
  }, [params, setParams]);

  /** The chat opens on the right, over the page (`chat=1` keeps it open on a reload). */
  const setChat = (open: boolean) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (open) next.set('chat', '1');
        else next.delete('chat');
        return next;
      },
      { replace: true },
    );

  const saveName = async (b: {
    title: string;
    icon: string | null;
    description: string | null;
    requirements: string[];
  }) => {
    if (!agent) return;
    const covers = [
      ...agent.covers.filter((c) => c.kind !== 'requirement'),
      ...b.requirements.map((key) => ({ kind: 'requirement', key })),
    ];
    const a = await unwrap(
      api.PUT(`${A}/{key}`, {
        params: { path },
        body: { title: b.title, icon: b.icon, description: b.description, covers },
      }),
    );
    setAgent(a);
    setNaming(false);
    setSaved(true);
  };

  const remove = async () => {
    if (!agent) return;
    const sites = (usage.data?.sites ?? []).filter((s) => s.subject === `agent:${agent.key}`);
    const n = (kinds: string[]) => sites.filter((s) => kinds.includes(s.kind)).length;
    const used = (
      [
        [n(['ask', 'llm']), 'place in apps', 'places in apps'],
        [n(['workflow']), 'workflow step', 'workflow steps'],
        [n(['test']), 'test', 'tests'],
      ] as const
    )
      .filter(([c]) => c > 0)
      .map(([c, one, many]) => `${c} ${c === 1 ? one : many}`);
    const ok = await confirm({
      title: `Remove “${agent.title}”?`,
      description:
        (used.length
          ? `It is still used: ${used.join(', ')} — they will fail until they are changed (see Used In). `
          : '') +
        'It is removed from this app with its tools and shares (one change). Its runs stay in the ledger.',
      confirmText: 'Remove',
    });
    if (!ok) return;
    try {
      await unwrap(api.DELETE(`${A}/{key}`, { params: { path } }));
      queryClient.removeQueries({ queryKey: agentKey });
      invalidate([k()]);
      navigate(host.paths.agents());
    } catch (e) {
      setErr(messageOf(e));
    }
  };

  const back = embedded ? null : (
    <Link
      to={host.paths.agents()}
      className="inline-flex items-center gap-1.5 text-sm text-page-text no-underline hover:underline"
      data-testid="agent-back"
    >
      <ArrowLeft size={14} /> Back to Agents
    </Link>
  );

  if (loadErr)
    return (
      <div className="p-6" data-testid="agent-page">
        {back}
        <div
          className="mt-4 rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-700"
          data-testid="agent-load-error"
        >
          {loadErr}
        </div>
      </div>
    );
  if (!agent)
    return (
      <div className="p-6" data-testid="agent-page">
        {back}
        <div className="flex items-center gap-2 text-sm text-page-muted mt-6">
          <Loader2 size={16} className="animate-spin" /> Loading the agent…
        </div>
      </div>
    );

  const apps = agent.covers.filter((c) => c.kind === 'app');
  const reqs = agent.covers.filter((c) => c.kind === 'requirement');
  const model = models.data?.roles.find((r) => r.role === agent.modelRole)?.model ?? null;
  const commit = doc?.source.kind === 'project' ? doc.source.commit : null;
  const questions = (suites.data ?? [])
    .filter((s) => s.subject === `agent:${agent.key}`)
    .flatMap((s) => s.cases.filter((c) => c.enabled !== false).map((c) => c.prompt))
    .slice(0, 3);
  const dot = <span aria-hidden>·</span>;

  return (
    <div className="px-6 py-4 max-w-[1400px]" data-testid="agent-page" data-agent-name={agent.key}>
      {back}
      <header className="flex flex-wrap items-start gap-4 mt-3 mb-5">
        <AgentIcon icon={agent.icon} size={36} className="mt-1 shrink-0" />
        <div className="flex-1 min-w-[min(100%,420px)]">
          <h1 className="text-2xl font-semibold text-page-title m-0 flex flex-wrap items-baseline gap-x-2">
            <span data-testid="agent-title">{agent.title}</span>
            <button
              type="button"
              className="inline-flex items-center gap-1 border-0 bg-transparent p-0 font-mono text-xs font-normal text-page-muted cursor-pointer hover:text-page-text"
              title="Its name in tests, workflows and the chat — click to copy"
              data-testid="agent-key"
              onClick={() => {
                void navigator.clipboard?.writeText(agent.key);
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1200);
              }}
            >
              {agent.key} {copied ? <Check size={11} /> : <Copy size={11} />}
            </button>
          </h1>
          {agent.description ? (
            <p className="text-page-muted mt-1 mb-0 max-w-3xl" data-testid="agent-description">
              {agent.description}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-xs text-page-muted">
            <span
              className="inline-flex flex-wrap items-center gap-1"
              data-testid="agent-works-over"
              title="Works over"
            >
              {apps.length || reqs.length ? (
                <>
                  {apps.map((c) => (
                    <span key={c.key} className="inline-flex items-center gap-1 text-page-text">
                      <AppWindow size={11} /> {c.key}
                    </span>
                  ))}
                  {reqs.map((c) => (
                    <span
                      key={c.key}
                      data-testid={`agent-covers-${c.key}`}
                      className="inline-flex items-center gap-1 text-page-text"
                    >
                      <FileCheck2 size={11} /> {c.key}
                    </span>
                  ))}
                </>
              ) : (
                'the project, through its tools'
              )}
            </span>
            {dot}
            <button
              type="button"
              className="inline-flex items-center gap-1 border-0 bg-transparent p-0 text-xs text-page-muted hover:text-page-text cursor-pointer"
              data-testid="agent-model-summary"
              title={`${roleLabel(agent.modelRole)} — from the project’s AI settings; open the Model tab`}
              onClick={() => setTab('model')}
            >
              <Cpu size={12} />
              {model ? <span className="font-mono">{model}</span> : `Model: ${roleLabel(agent.modelRole)}`}
            </button>
            {dot}
            <span className="inline-flex items-center gap-1" data-testid="agent-visibility">
              {agent.visibility === 'organization' ? <Globe size={12} /> : <Lock size={12} />}
              {agent.visibility === 'organization' ? 'Organization' : 'Private'}
            </span>
            {dot}
            <span data-testid="agent-changed" title={commit ? `version ${commit.slice(0, 7)}` : undefined}>
              Changed {ago(agent.updatedAt)}
              {agent.updatedBy ? ` by ${agent.updatedBy}` : ''}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
          {host.ChangeRequest ? (
            <button
              type="button"
              className={btnGhost}
              data-testid="agent-change-request"
              onClick={() => setChanging(true)}
            >
              <Sparkles size={14} /> Change with the builder
            </button>
          ) : null}
          <button type="button" className={btnPrimary} data-testid="agent-chat" onClick={() => setChat(true)}>
            <MessageSquare size={14} /> Chat
          </button>
          {agent.canManage ? (
            <div className="relative">
              <button
                type="button"
                className={btnGhost}
                title="More"
                data-testid="agent-more"
                onClick={() => setMenu(!menu)}
              >
                <MoreVertical size={14} />
              </button>
              {menu ? (
                <div
                  className="absolute right-0 top-full z-30 mt-1 w-60 whitespace-nowrap rounded-md border border-card-border bg-card-bg p-1 shadow-lg"
                  data-testid="agent-more-menu"
                >
                  <button
                    type="button"
                    className={menuItem}
                    data-testid="agent-edit"
                    onClick={() => {
                      setMenu(false);
                      setSaved(false);
                      setNaming(true);
                    }}
                  >
                    <Pencil size={14} /> Name and description…
                  </button>
                  <button
                    type="button"
                    className={menuItem}
                    data-testid="agent-share"
                    onClick={() => {
                      setMenu(false);
                      setSharing(true);
                    }}
                  >
                    {agent.visibility === 'organization' ? <Globe size={14} /> : <Lock size={14} />} Who can
                    use it…
                  </button>
                  <button
                    type="button"
                    className={menuItem + ' text-red-600'}
                    data-testid="agent-remove"
                    onClick={() => {
                      setMenu(false);
                      void remove();
                    }}
                  >
                    <Trash2 size={14} /> Remove agent
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      {err ? (
        <div
          className="mb-4 rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-700"
          data-testid="agent-error"
        >
          {err}
        </div>
      ) : null}
      {saved ? (
        <div
          className="mb-4 rounded-md border border-green-300 bg-green-50 dark:bg-green-950/30 p-3 text-sm text-green-800 dark:text-green-200"
          data-testid="agent-saved"
        >
          Saved as one change.
        </div>
      ) : null}

      {doc ? (
        <AgentTabs
          ctx={{
            // The page's agent is the latest (a save answers it before the catalog reloads).
            agent: { ...doc, model: { ...doc.model, role: agent.modelRole } },
          }}
          tab={tab}
          setTab={setTab}
        />
      ) : (
        <div className="flex items-center gap-2 text-sm text-page-muted" data-testid="agent-tabs-loading">
          {agentDocs.error ? (
            `The agent’s tabs could not be read: ${String(agentDocs.error)}`
          ) : (
            <>
              <Loader2 size={14} className="animate-spin" /> Loading…
            </>
          )}
        </div>
      )}

      {chatOpen ? (
        <aside
          className="fixed inset-y-0 right-0 z-40 flex w-[440px] max-w-full flex-col border-l border-card-border bg-card-bg shadow-2xl"
          data-testid="agent-chat-panel"
        >
          <div className="flex items-start gap-2 border-b border-card-border px-4 py-3">
            <div className="min-w-0">
              <div className="font-semibold text-page-title truncate">
                <MessageSquare size={15} className="inline mr-1.5 text-page-muted" />
                Try {agent.title}
              </div>
              <div className="text-xs text-page-muted">
                It answers here the same way it does in apps and workflows.
              </div>
            </div>
            <button
              type="button"
              className={iconBtn + ' ml-auto'}
              title="Close"
              data-testid="agent-chat-close"
              onClick={() => setChat(false)}
            >
              <X size={14} />
            </button>
          </div>
          <div className="flex-1 overflow-auto p-4">
            <TestChat
              agentKey={agent.key}
              title={agent.title}
              onTurn={reloadRuns}
              suggestions={questions}
              footer={
                <>
                  <Cpu size={11} className="inline mr-1" />
                  {model ?? roleLabel(agent.modelRole)} · uses what is saved
                </>
              }
            />
          </div>
        </aside>
      ) : null}

      {naming ? <NameDialog agent={agent} onSave={saveName} onClose={() => setNaming(false)} /> : null}
      {sharing ? <ShareDialog agent={agent} onClose={() => setSharing(false)} onChanged={setAgent} /> : null}
      {changing && host.ChangeRequest ? (
        <host.ChangeRequest agent={agent.key} title={agent.title} onClose={() => setChanging(false)} />
      ) : null}
    </div>
  );
}
